import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { DeveloperService } from "@shared/services/developer.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BaseSkeletonEnum } from "@shared/enums/skeleton.enum";
import { ActivatedRoute } from "@angular/router";
import { TeamService } from "@shared/services/team.service";
import { CustomEventService } from "@shared/services/custom-event.service";
import { searchFieldsOnTable } from "@shared/utils/search.util";
import { filterListMapper } from "@shared/utils/scorecard.util";
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { fromJS } from "immutable";
import { AdminService } from "@shared/services/admin.service";
import { PropertiesService } from "@core/services/properties.service";
import { StorageService } from "@core/services/storage.service";

@UntilDestroy()
@Component({
  selector: 'app-manage-member-modal',
  templateUrl: './manage-member-modal.component.html'
})
export class ManageMemberModalComponent implements OnInit, OnDestroy {
  loading: boolean;
  developers: any[];
  developersDisplayData: any[];
  tooltipContent: any;
  searchValue = '';
  baseSkeletonEnum = BaseSkeletonEnum;
  developersListLoading: boolean;
  selectedDeveloper;
  developersList: any;
  teamData: any;
  developerIds: any;
  developerWithId: any;
  isButtonVisible: any;

  listOfCompanyRole: Base.FilterObjectModel[] = [];
  isVisible = false;

  visible = false;

  individualScorecard: any;
  featuresData: any;

  pageSize = 10;
  pageIndex = 1;
  total = 0;

  @Input() title;
  @Input() teamId;

  @ViewChild('toolTipTitle', { static: true }) toolTipTitle: TemplateRef<any>;

  constructor(private developerService: DeveloperService,
              private route: ActivatedRoute,
              private adminService: AdminService,
              private storageService: StorageService,
              private customEventService: CustomEventService,
              public propertiesService: PropertiesService,
              private teamService: TeamService) {
  }

  ngOnInit(): void {
    this.selectedDeveloper = [];
    this.initialMemberData();
    this.getTeamInfo();
    this.getIndividualScorecardActive();
  }

  selectDevelopers($event): void {
    this.selectedDeveloper = $event;
  }

  getDeveloperLabelInactive(item: any): string {
    const label = item.name + ' ' + item.surname;
    if (!item.enabled) {
      return label + ' ' + '(Inactive)';
    }
    return label;
  }

  initialMemberData(): void {
    this.loading = true;
    this.teamService.getMemberList(this.teamId).pipe(untilDestroyed(this))
      .subscribe(res => {
        this.developers = res;
        this.developersDisplayData = res.sort((a, b) => (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1);
        this.listOfCompanyRole = Array.from(new Set(this.developers.map(item => item.companyRole.label))).map(filterListMapper);
        const maxPageIndex = Math.ceil(this.developers.length / this.pageSize);
        if (this.pageIndex > maxPageIndex) {
          this.pageIndex = Math.max(maxPageIndex, 1);
        }
        this.loading = false;
        this.refreshDisplayedData();
      }, error => {
        this.loading = false;
        console.log('error while get member data', error);
      });
  }

  pageIndexChange($event): void {
    this.pageIndex = $event;
    this.refreshDisplayedData();
  }

  refreshDisplayedData() {
    const sortedDevelopers = [...this.developers].sort((a, b) => {
      if (a.enabled !== b.enabled) {
        return a.enabled ? -1 : 1;
      }

      const nameA = a.memberName?.toLowerCase() || '';
      const nameB = b.memberName?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    const startIndex = (this.pageIndex - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, sortedDevelopers.length);
    this.developersDisplayData = sortedDevelopers.slice(startIndex, endIndex);

  }

  getTeamInfo(): void {
    this.teamService.get(this.teamId)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.teamData = res;
        const leadList = res.leads.map(item => item.id);
        this.developerIds = this.teamData.developers.map(item => item.id);
       this.isButtonVisible = leadList.filter(id => this.developerIds.includes(id));
        this.developersListLoading = true;
        this.developerService.list()
          .pipe(untilDestroyed(this))
          .subscribe(response => {
            this.developersList = response.sort((a, b) => (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1)
            this.developersListLoading = false;

              this.developerWithId= Array.from(new Set(this.developerIds.map(data => data))).map(item => {
                return {
                  id: item,
                }
              });

            let i = this.developersList.length;
            while(i--) {
              if (this.developerWithId.some(({id}) => this.developersList[i].id === id)) {
                this.developersList.splice(i,1)
              }
            }

          }, error => {
            this.developersListLoading = false;
            console.log('error occurred while fetching developer list data', error);
          });
      })
  }

  delete(data: any): void {
    delete this.teamData.scorecardId;
    delete this.teamData.managers;
    delete this.teamData.directors;
    this.teamData.includedTeams = [];
    this.teamData.leads = this.teamData.leads.map(item => item.id)
    this.teamData.developers = this.developerIds.filter(x => x !== data.memberId);
    this.teamService.updateTeam(this.teamData, this.teamId)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.initialMemberData();
          this.getTeamInfo();
          this.customEventService.announceEvent( ActionTypesEnum.UPDATE_TEAM, fromJS(this.teamData));

        }, error => {
          console.log('error while deleting member', error);
        });
  }

  addDeveloper(): void {
    if (this.selectedDeveloper.length > 1) {
      for (const m of this.selectedDeveloper) {
        this.developerIds.push(m)
      }
    } else {
      this.developerIds.push(this.selectedDeveloper.toString());
    }

    delete this.teamData.scorecardId;
    delete this.teamData.managers;
    delete this.teamData.directors;
    this.teamData.includedTeams = [];
    this.teamData.leads = this.teamData.leads.map(item => item.id)

   this.teamData.developers = [].concat(this.developerIds);
    this.teamService.updateTeam(this.teamData, this.teamId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initialMemberData();
        this.getTeamInfo();
        this.selectedDeveloper.length = 0;

        this.customEventService.announceEvent( ActionTypesEnum.UPDATE_TEAM, fromJS(this.teamData));

      }, error => {
        console.log('error while adding member', error);
      });
  }

  routeDeveloperPage(developer): void {
    if (this.individualScorecard){
      window.open('/scorecard/developer/' + developer.memberId, "_ blank")
    }
  }

  search(): void {
      const initialCompletedData = this.developers;

      const filterFunctionCompleted = (rowData: any): boolean => {
        const filterControl = (filterList, key) => {
          const selectedFilters = filterList.filter(item => item.selected);
          return selectedFilters.length ? selectedFilters.some(item => item.text === rowData[key].label) : true
        };

        return filterControl(this.listOfCompanyRole, "companyRole")
      };

      const data: any[] = searchFieldsOnTable(initialCompletedData, this.searchValue, ["memberName"]).filter(filterFunctionCompleted);

      this.developersDisplayData = data;

  }
  getIndividualScorecardActive(): void {
    this.individualScorecard = JSON.parse(this.storageService.get('features_data_individual'));
    this.loading = false;
  }
  filterChangeCompanyRole($event: string[], type: string): void {
    const selectedMapper = (item: Base.FilterObjectModel): Base.FilterObjectModel => {
      item.selected = $event.indexOf(item.text) > -1;
      return item;
    };

    switch (type) {
      case "companyRole":
        this.listOfCompanyRole.map(selectedMapper);
        break;
    }

    this.search();
  }

  addDeveloperVisible(): void {
    this.isVisible = true;
  }

  ngOnDestroy() {}


}
